import { fetchUtils, UserIdentity } from 'react-admin';
import {
  AvailabilityResponse,
  GetAudienceByIdResult,
  GetUserAccountByIdResult,
  GetUserAccountSearchResult,
  GetUserGroupsByIdParams,
  GetUserGroupsByIdResult,
  GetUserMeFullResponse,
  GetUserMeGroupsResponse,
  UpdateUserGroupsByIdResult,
  GetGroupSearchParams,
  GetGroupSearchResult,
  UserAccountSearchParams,
  UserAccountSummary,
  ResourceType,
  ExportResponse,
  ExportStatusResponse,
  ExportModuleResponse,
  UpdateUserAccountByIdParams,
  StatusOnlyResponse,
} from 'utility/types/dataProvider';
import { qsOptions, stringifyOpts } from 'providers/clients/httpClient';
import { httpClient } from 'providers/clients/httpClient';
import { hasValue } from 'utility/functions';
import qs from 'qs';

// -----------------------------------------------
// ------------ USERS PROVIDER -------------------
// -----------------------------------------------
const domainSubscriptionSupportCache: Record<string, boolean> = {};

export const usersProvider = {
  //  [GET] user accounts accross domains
  getUserAccountSearchAcrossDomains: async (
    domains: string[],
    params: UserAccountSearchParams,
    pageMap: Record<string, string | undefined> = {},
  ): Promise<{
    items: UserAccountSummary[];
    nextPages: Record<string, string | undefined>;
  }> => {
    try {
      const results: UserAccountSummary[] = [];
      const nextPages: Record<string, string | undefined> = {};

      for (const domain of domains) {
        const page = pageMap[domain];

        const buildQueryParams = (includeSubscription: boolean) => ({
          ...(params.query && { query: params.query }),
          ...(params.account_type && { account_type: params.account_type }),
          ...(params.account_status && { account_status: params.account_status }),
          ...(params.auth_type && { auth_type: params.auth_type }),
          ...(params.banned && { banned: params.banned }),
          ...(page && { page }),
          ...(params.sort && { sort: params.sort }),
          ...(params.dir && { dir: params.dir }),
          ...(params.limit && { limit: params.limit > 100 ? 99 : params.limit }),
          ...(includeSubscription && { subscription: true }),
        });

        const attemptFetch = async (withSubscription: boolean) => {
          const queryParams = buildQueryParams(withSubscription);
          const path = `/user/account/search/?${fetchUtils.queryParameters(
            queryParams,
            stringifyOpts,
          )}`;

          return await httpClient({
            url: path,
            method: 'GET',
            headers: { 'X-Blox-Domain': domain },
          });
        };

        try {
          if (!(domain in domainSubscriptionSupportCache)) {
            try {
              const configResp = await httpClient({
                url: '/core/session/config/',
                method: 'GET',
                headers: { 'X-Blox-Domain': domain },
              });

              const subscriptionKeyExists = Object.prototype.hasOwnProperty.call(
                configResp?.data?.data?.software_privileges || {},
                'subscription',
              );

              domainSubscriptionSupportCache[domain] = subscriptionKeyExists;
            } catch (err) {
              console.warn(
                `[domain:${domain}] failed to fetch config for subscription check`,
                err,
              );
              // fallback: assume subscription is not supported
              domainSubscriptionSupportCache[domain] = false;
            }
          }

          const includeSubscription = domainSubscriptionSupportCache[domain];

          const { data } = await attemptFetch(includeSubscription);

          const items = (data?.data?.items || []).map((item: UserAccountSummary) => ({
            ...item,
            domain,
            id: item.uuid,
          }));

          results.push(...items);
          nextPages[domain] = data?.data?.next || undefined;
        } catch (error) {
          console.error(`[domain:${domain}] failed fetching user accounts`, error);
          nextPages[domain] = undefined;
        }

        // Only fetch one domain (the current one), once per scroll
        break;
      }

      return { items: results, nextPages };
    } catch (error) {
      console.error('[getUserAccountSearchAcrossDomains] Error:', error);
      throw error;
    }
  },

  // [GET] /user/me/ Retrieve the active users profile
  getUserMe: async (): Promise<GetUserMeFullResponse> => {
    try {
      const path = `/user/me/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetUserMeFullResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getUserMe] Error:', error);
      throw error;
    }
  },

  // [POST] /user/group/ - Create a new admin group
  createGroup: async (payload: {
    name: string;
    rules: Array<{
      id?: number | null;
      app: string;
      permission: string;
      options?: string[];
    }>;
  }) => {
    try {
      const path = '/user/group/';
      const { data, status } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });
      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[createGroup] Error:', error);
      throw error;
    }
  },

  // [GET] /user/group/{group_id}/ - Retrieve a group by ID
  getGroupById: async (groupId: string | number) => {
    try {
      const path = `/user/group/${groupId}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });
      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getGroupById] Error:', error);
      throw error;
    }
  },

  // [PUT] /user/group/{group_id}/ - Update a group by ID
  updateGroupById: async (
    groupId: string | number,
    payload: {
      name: string;
      rules: Array<{
        id?: number | null;
        app: string;
        permission: string;
        options?: string[];
      }>;
    },
  ) => {
    try {
      const path = `/user/group/${groupId}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(payload),
      });
      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[updateGroupById] Error:', error);
      throw error;
    }
  },

  // [DELETE] /user/group/{group_id}/ - Delete a group by ID
  deleteGroupById: async (groupId: string | number) => {
    try {
      const path = `/user/group/${groupId}/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });
      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[deleteGroupById] Error:', error);
      throw error;
    }
  },

  // [GET] user/account/search
  getUserAccountSearch: async (
    params: UserAccountSearchParams,
  ): Promise<GetUserAccountSearchResult> => {
    try {
      const abortSignal = params?.signal;
      const payload: Partial<UserAccountSearchParams> = {};

      // Build base payload via provided parameters
      if (params?.query) {
        payload.query = params.query;
      }
      if (params?.account_type) {
        payload.account_type = params.account_type;
      }
      if (params?.account_status) {
        payload.account_status = params.account_status;
      }
      if (params?.auth_type) {
        payload.auth_type = params.auth_type;
      }
      // Booleans (can be explicitly false)
      if (hasValue(params.banned)) {
        payload.banned = params.banned;
      }
      if (hasValue(params.subscription)) {
        payload.subscription = params.subscription;
      }
      if (params?.subscription_state) {
        payload.subscription_state = params.subscription_state;
      }
      if (params?.subscription_service) {
        payload.subscription_service = params.subscription_service;
      }

      if (params?.subscription_service_type) {
        payload.subscription_service_type = params.subscription_service_type;
      }
      // Deeper objects - check depth before assignment
      if (params?.subscription_start_time?.min) {
        payload.subscription_start_time ??= {};
        payload.subscription_start_time.min = params.subscription_start_time.min;
      }
      if (params?.subscription_start_time?.max) {
        payload.subscription_start_time ??= {};
        payload.subscription_start_time.max = params.subscription_start_time.max;
      }
      if (params?.subscription_rate_price_range?.min) {
        payload.subscription_rate_price_range ??= {};
        payload.subscription_rate_price_range.min =
          params.subscription_rate_price_range.min;
      }
      if (params?.subscription_rate_price_range?.max) {
        payload.subscription_rate_price_range ??= {};
        payload.subscription_rate_price_range.max =
          params.subscription_rate_price_range.max;
      }
      if (params?.subscription_rate_id) {
        payload.subscription_rate_id = params.subscription_rate_id;
      }
      if (params?.subscription_expire_time?.min) {
        payload.subscription_expire_time ??= {};
        payload.subscription_expire_time.min = params.subscription_expire_time.min;
      }
      if (params?.subscription_expire_time?.max) {
        payload.subscription_expire_time ??= {};
        payload.subscription_expire_time.max = params.subscription_expire_time.max;
      }
      if (params?.subscription_duration) {
        payload.subscription_duration = params.subscription_duration;
      }
      if (params?.view_by) {
        payload.view_by = params.view_by;
      }
      if (params?.linked) {
        payload.linked = params.linked;
      }
      if (params?.export_app) {
        payload.export_app = params.export_app;
      }
      if (params?.export_type) {
        payload.export_type = params.export_type;
      }
      if (params?.limit) {
        payload.limit = Math.min(params.limit, 100);
      }
      if (params?.page) {
        payload.page = params.page;
      }
      if (params?.sort) {
        payload.sort = params.sort;
      }
      if (params?.dir) {
        payload.dir = params.dir;
      }

      const baseUrl = `/user/account/search/`;
      const hasPayload = Object.keys(payload).length > 0;
      const qpStr = hasPayload ? qs.stringify(payload, qsOptions) : '';
      const finalUrl = qpStr ? `${baseUrl}?${qpStr}` : baseUrl;

      const { data, status } = await httpClient({
        url: finalUrl,
        method: 'GET',
        signal: abortSignal,
      });

      const result: GetUserAccountSearchResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          items: data?.data?.items || [],
        },
      };

      return result;
    } catch (error) {
      console.error('[getUserAccountSearch]:', error);
      throw error;
    }
  },

  // Get user/author account summary by ID
  getUserAccountSummaryById: async (uuid: string): Promise<GetUserAccountByIdResult> => {
    try {
      const path = `/user/account/${uuid}/summary/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetUserAccountByIdResult = {
        status: data?.status || status,
        data: data?.data || {},
      };

      return result;
    } catch (error) {
      console.error('[getUserAccountSummaryById] Error:', error);
      throw error;
    }
  },

  getUserAccountById: async (uuid: string): Promise<UserIdentity> => {
    try {
      const path = `/user/account/${uuid}/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: UserIdentity = data?.data;
      return result;
    } catch (error) {
      console.error('[getUserAccountById] Error:', error);
      throw error;
    }
  },

  // Get the groups the active user is assigned to
  getActiveUserGroups: async (): Promise<GetUserMeGroupsResponse> => {
    try {
      const path = `/user/me/groups/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetUserMeGroupsResponse = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[getActiveUserGroups] Error:', error);
      throw error;
    }
  },

  // Retrieve a light summary of the active user's account
  getActiveUserSummary: async (): Promise<UserAccountSummary> => {
    try {
      const path = `/user/me/summary/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: UserAccountSummary = {
        uuid: data?.data?.uuid,
        account_type: data?.data?.account_type,
        email: data?.data?.email,
        screen_name: data?.data?.screen_name,
        avatar: data?.data?.avatar,
        first_name: data?.data?.first_name,
        last_name: data?.data?.last_name,
        title: data?.data?.title,
        department: data?.data?.department,
        subscription: data?.data?.subscription,
        ...data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getActiveUserSummary] Error:', error);
      throw error;
    }
  },

  // User: Send instant login email
  sendInstantLogin: async (uuid: string, params?: any) => {
    try {
      const path = `/user/account/${uuid}/instant/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(params),
      });

      return data;
    } catch (error) {
      console.error('[sendInstantLogin] Error:', error);
      throw error;
    }
  },

  // User: Send password reset email
  sendPasswordReset: async (uuid: string, params?: any) => {
    try {
      const path = `/user/account/${uuid}/auth/password/reset/`;

      const response = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(params),
      });

      return response;
    } catch (error) {
      console.error('[sendPasswordReset] Error:', error);
      throw error;
    }
  },

  // User: Set password
  setPassword: async (uuid: string, params?: any) => {
    try {
      const path = `/user/account/${uuid}/auth/password/`;

      const response = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(params),
      });

      return response;
    } catch (error) {
      console.error('[setPassword] Error:', error);
      throw error;
    }
  },

  // User: check availability of screen_name or email
  checkAvailability: async (
    name: 'screen_name' | 'email',
    value: string,
  ): Promise<AvailabilityResponse> => {
    try {
      const encodedValue = encodeURIComponent(value);
      const path = `/user/account/available/${name}/?${name}=${encodedValue}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const formattedResult = {
        status: data?.status || status,
        available: data?.data?.available,
      };
      return formattedResult;
    } catch (error) {
      console.error('[checkAvailability] Error:', error);
      return {
        status: 'error',
      };
    }
  },

  // Get /user/group/names/
  // a list of all admin group names on the site
  getUserGroupNames: async (): Promise<string[] | null> => {
    try {
      const path = `/user/group/names/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const groupNames: string[] = Object.values(data?.data || {}).filter(
        (value): value is string => typeof value === 'string',
      );
      return groupNames;
    } catch (error) {
      console.error('[getUserGroupNames] Error:', error);
      throw error;
    }
  },

  // [GET] /user/group/config/ - Get assignable permissions for admin group management
  getGroupConfig: async () => {
    try {
      const path = '/user/group/config/';
      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status || status,
        data: data?.data,
      };
    } catch (error) {
      console.error('[getGroupConfig] Error:', error);
      throw error;
    }
  },

  // [GET] /user/group/ - Search admin groups
  getGroupSearch: async (params: GetGroupSearchParams): Promise<GetGroupSearchResult> => {
    try {
      const payload = {
        ...(params.limit && { limit: params.limit }),
        ...(params.page && { page: params.page }),
      };

      const path = `/user/group/?${fetchUtils.queryParameters(payload, stringifyOpts)}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetGroupSearchResult = {
        status: data?.status || status,
        data: {
          total: data?.data?.total,
          next: data?.data?.next,
          prev: data?.data?.prev,
          facets: data?.data?.facets || {},
          items: data?.data?.items?.map((item: any) => ({
            id: item?.id,
            name: item?.name,
            // NOTE: The backend schema and openapi types declare 'member_count', but the actual data uses 'members'.
            // This is a known schema issue. Use 'members' for consistency with the real data.
            members: item?.members ?? item?.member_count,
            ...item,
          })),
        },
      };
      return result;
    } catch (error) {
      console.error('[getGroupSearch] Error:', error);
      throw error;
    }
  },

  // Update /user/admin/{id}/groups/
  updateUserGroupsById: async (uuid: string, groups: string[]) => {
    try {
      const path = `/user/admin/${uuid}/groups/?${fetchUtils.queryParameters(
        {},
        stringifyOpts,
      )}`;

      const requestBody = JSON.stringify(groups);

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: requestBody,
      });

      const result: UpdateUserGroupsByIdResult = {
        status: data?.status || status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[updateUserGroupsById] Error:', error);
      throw error;
    }
  },

  // Get /user/admin/{id}/groups/
  getUserGroupsById: async ({
    uuid,
  }: GetUserGroupsByIdParams): Promise<GetUserGroupsByIdResult> => {
    try {
      if (!uuid) {
        throw new Error('[getUserGroupsById] No id provided for query.');
      }

      const path = `/user/admin/${uuid}/groups/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetUserGroupsByIdResult = {
        status: data?.status,
        data: data?.data,
      };
      return result;
    } catch (error) {
      console.error('[getUserGroupsById] Error:', error);
      throw error;
    }
  },

  updateUserData: async (uuid: string, params: any): Promise<UserAccountSummary> => {
    try {
      const path = `/user/account/${uuid}/`;

      const { data } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(params),
      });

      const result: UserAccountSummary = {
        uuid: data?.data?.uuid,
        account_type: data?.data?.account_type,
        email: data?.data?.email,
        screen_name: data?.data?.screen_name,
        avatar: data?.data?.avatar,
        first_name: data?.data?.first_name,
        last_name: data?.data?.last_name,
        title: data?.data?.title,
        department: data?.data?.department,
        subscription: data?.data?.subscription,
        ...data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateUserData] Error:', error);
      throw error;
    }
  },

  updateUserAvatar: async (
    uuid: string,
    params: UpdateUserAccountByIdParams,
  ): Promise<StatusOnlyResponse> => {
    try {
      const path = `/user/account/${uuid}/avatar/`;

      const { data, status } = await httpClient({
        url: path,
        method: 'PUT',
        data: JSON.stringify(params),
      });

      // Status 204 if successful
      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[updateUserAvatar] Error:', error);
      throw error;
    }
  },

  deleteUserAvatar: async (uuid: string): Promise<StatusOnlyResponse> => {
    try {
      const path = `/user/account/${uuid}/avatar/`;
      const { data, status } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      // Status 204 if successful
      const result = {
        status: data?.status || status,
        data: data?.data,
      };

      return result;
    } catch (error) {
      console.error('[deleteUserAvatar] Error:', error);
      throw error;
    }
  },

  // Downgrade User: Remove/downgrade admin access from user.
  downgradeAdmin: async (uuid: string) => {
    try {
      const path = `/user/admin/${uuid}/`;

      const response = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return response;
    } catch (error) {
      console.error('[downgradeAdmin] Error:', error);
      throw error;
    }
  },

  // Get /dmp/audiences/{id}/
  getAudienceById: async (id: string): Promise<GetAudienceByIdResult> => {
    try {
      const path = `/dmp/audiences/${id}/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      const result: GetAudienceByIdResult = {
        status: data?.status,
        data: { ...data?.data },
      };
      return result;
    } catch (error) {
      console.error('[getAudienceById] Error:', error);
      throw error;
    }
  },

  // User: Send administrator invitation email.
  postAdminInvite: async (payload: { invitee_email: string; groups?: string[] }) => {
    try {
      const path = `/user/admin/invite/`;

      const response = await httpClient({
        url: path,
        method: 'POST',
        data: JSON.stringify(payload),
      });

      return response;
    } catch (error) {
      console.error('[postAdminInvite] Error:', error);
      throw error;
    }
  },

  // [GET] /user/account/{id}/linked/
  getLinkedAccounts: async (userId: string) => {
    try {
      const path = `/user/account/${userId}/linked/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      return data;
    } catch (error) {
      console.error('[getLinkedAccounts] Error:', error);
      throw error;
    }
  },

  // [POST] /user/account/{id}/linked/{linked_user_id}/
  linkUser: async (primaryUserId: string, linkedUserId: string) => {
    try {
      const path = `/user/account/${primaryUserId}/linked/${linkedUserId}/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
      });

      return data;
    } catch (error) {
      console.error('[linkUser] Error:', error);
      throw error;
    }
  },

  // [DELETE] /user/account/{id}/linked/{linked_user_id}/
  unlinkUser: async (primaryUserId: string, linkedUserId: string) => {
    try {
      const path = `/user/account/${primaryUserId}/linked/${linkedUserId}/`;

      const { data } = await httpClient({
        url: path,
        method: 'DELETE',
      });

      return data;
    } catch (error) {
      console.error('[unlinkUser] Error:', error);
      throw error;
    }
  },

  // [POST] /user/account/{id}/linked/{linked_user_id}/approve/
  approveLinkedUser: async (primaryUserId: string, linkedUserId: string) => {
    try {
      const path = `/user/account/${primaryUserId}/linked/${linkedUserId}/approve/`;

      const { data } = await httpClient({
        url: path,
        method: 'POST',
      });

      return data;
    } catch (error) {
      console.error('[approveLinkedUser] Error:', error);
      throw error;
    }
  },

  /**
   * Get available export modules
   * @returns Promise<GetExportModulesResponse>
   */
  getExportModules: async (): Promise<ExportModuleResponse> => {
    try {
      const path = `/user/account/export/modules/`;

      const { data } = await httpClient({
        url: path,
        method: 'GET',
      });

      return {
        status: data?.status,
        data: data?.data || [],
      };
    } catch (error) {
      console.error('[getExportModules] Error:', error);
      throw error;
    }
  },

  /**
   * Generic function to initiate an export for any supported resource type
   * @param resourceType - Type of resource to export (user, subscription, etc.)
   * @param params - Additional parameters for the export
   */
  initiateExport: async (
    resourceType?: ResourceType,
    params: Record<string, any> = {},
  ): Promise<ExportResponse> => {
    try {
      if (!resourceType) {
        throw new Error('Resource type is required.');
      }

      // Build the query parameters for export
      const exportParams = {
        export_app: resourceType,
        export_type: params.export_type || 'accounts',
        ...params,
        limit: 1, // Just to trigger the export
      };

      const path = `/user/account/search/?${fetchUtils.queryParameters(
        exportParams,
        stringifyOpts,
      )}`;

      const { data, status } = await httpClient({
        url: path,
        method: 'GET',
      });

      // Extract export ID depending on the response format
      let exportId = null;
      let isReady = false;

      if (typeof data === 'string') {
        // Direct string response
        exportId = data;
        isReady = true;
      } else if (data && typeof data.data === 'string') {
        // Nested data property with string
        exportId = data.data;
        isReady = true;
      } else if (data && data.status === 'success' && typeof data.data === 'string') {
        // Success response with data string
        exportId = data.data;
        isReady = true;
      }

      return {
        exportId,
        isReady,
        status,
        resourceType,
        data,
      };
    } catch (error) {
      console.error(`[initiateExport:${resourceType}] Error:`, error);
      throw error;
    }
  },

  /**
   * Generic function to check export status for any resource type
   * @param id - Export ID to check
   * @param resourceType - Type of resource (user, subscription, etc.)
   */
  getExportUserStatus: async (
    id: string,
    resourceType?: ResourceType,
  ): Promise<ExportStatusResponse> => {
    try {
      if (!id) {
        throw new Error('Export ID is required to check status');
      }

      if (!resourceType) {
        throw new Error('Resource type is required.');
      }

      const path =
        resourceType === 'transactions'
          ? `/pay/transactions/export/${id}/`
          : `/user/account/export/download/${id}/`;

      // Transactions export uses POST for status; others use HEAD.
      const response = await httpClient({
        url: path,
        method: resourceType === 'transactions' ? 'POST' : 'HEAD',
        headers: {
          Accept: 'text/csv',
        },
        data: resourceType === 'transactions' ? {} : undefined,
      });

      return {
        status: response.status,
        exportId: id,
        isReady: response.status === 200,
        resourceType,
      };
    } catch (error: any) {
      // Explicitly type error as any to safely access properties
      console.error(`[getExportStatus:${resourceType}] Error:`, error);

      // Return a structured error response
      return {
        status:
          error && error.response && error.response.status ? error.response.status : 500,
        exportId: id,
        isReady: false,
        resourceType,
      };
    }
  },

  /**
   * Generic function to download an export for any resource type
   * @param id - Export ID to download
   * @param resourceType - Type of resource (user, subscription, etc.)
   */
  getExportDownload: async (
    id: string,
    resourceType?: ResourceType,
  ): Promise<ExportResponse> => {
    try {
      if (!id) {
        throw new Error('Export ID is required to download export');
      }

      if (!resourceType) {
        throw new Error('Resource type is required to download export');
      }

      const path =
        resourceType === 'transactions'
          ? `/pay/transactions/export/${id}/`
          : `/user/account/export/download/${id}/`;

      // Make the request without setting responseType: 'blob' yet
      // so we can check the status without triggering a download
      const response = await httpClient({
        url: path,
        method: resourceType === 'transactions' ? 'POST' : 'GET',
        responseType: 'blob',
        headers: {
          Accept: 'text/csv',
        },
        data: resourceType === 'transactions' ? {} : undefined,
      });

      // Return the response with status and data
      return {
        status: response.status,
        exportId: id,
        isReady: response.status === 200,
        data: response.data,
        resourceType,
      };
    } catch (error) {
      console.error(`[getExportDownload:${resourceType}] Error:`, error);
      throw error;
    }
  },
};
